import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import cn from "clsx";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";

import { TOAST_TYPES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getWishListGeneralDataStore } from "@/util";
import {
  selectDeleteWishListItemLoaded,
  selectWishListAndCartGeneralLoaded,
  selectWishListGeneralProducts
} from "@/util/selectors";
import useBreakpoint from "../../../hooks/useBreakpoint";

import {
  saveToWishlist,
  deleteWishListData
} from "@/redux/actions/wishlist.action";
import { getCartAndWishlistGeneralData } from "@/redux/actions/cart.action";
import { toastMsg } from "@/util/toast";
import { checkResponseStatus } from "@/util/common";
import PdpAnalyticsEvent from "../../../services/analytics/main/pdp";
import DialogModal from "@/components/dialogModal";
import { DynamicYieldEventService } from "@/services";

const DELAY_TIME = 1000;

type WishlistProductType = {
  data: {
    productId: number;
    isMultisize: boolean;
    sizeType: string;
    sizeTypeKey: string;
    size: string;
    sizeId: number;
    _id: string;
  };
};

const AddToWishlistButton = ({
  product,
  iconRef = null,
  selectedSizeFullData,
  isQuickLookModal = false
}) => {
  const { t } = useTranslation("productDetails");
  const { isMobileView } = useBreakpoint();
  const [alertMessage, setAlertMessage] = useState("");

  const item = { ...product, productId: product.id };
  const [wishlistAnimationClass, setWishlistAnimationClass] = useState("");
  const prevPropsRef = useRef(null);
  const wishListBaseData = useAppSelector(selectWishListGeneralProducts);

  const wishListAndCartBaseDataLoaded = useAppSelector(
    selectWishListAndCartGeneralLoaded
  );
  const deleteWishListItemLoaded = useAppSelector(
    selectDeleteWishListItemLoaded
  );

  const dispatch = useAppDispatch();

  const removeAnimationClass = () =>
    setTimeout(() => {
      setWishlistAnimationClass("");
    }, DELAY_TIME);

  useEffect(() => {
    const checkOnWishListAndCartBaseData =
      !prevPropsRef?.current?.wishListAndCartBaseDataLoaded &&
      wishListAndCartBaseDataLoaded;
    const checkOnDeleteWishListItem =
      !prevPropsRef?.current?.deleteWishListItemLoaded &&
      deleteWishListItemLoaded;

    if (
      wishlistAnimationClass &&
      (checkOnWishListAndCartBaseData || checkOnDeleteWishListItem)
    ) {
      removeAnimationClass();
    }

    prevPropsRef.current = {
      wishListAndCartBaseDataLoaded,
      deleteWishListItemLoaded
    };
  }, [
    wishlistAnimationClass,
    deleteWishListItemLoaded,
    wishListAndCartBaseDataLoaded
  ]);

  const isPresentInWishlist = useCallback(
    product => {
      const isPresent = wishListBaseData?.find(wishlistProduct => {
        if (selectedSizeFullData?.size?.size) {
          return (
            wishlistProduct.productId === product.id &&
            wishlistProduct.sizeId === selectedSizeFullData.size.sizeId
          );
        } else {
          return (
            wishlistProduct.productId === product.id && !wishlistProduct.size
          );
        }
      });

      return Boolean(isPresent);
    },
    [wishListBaseData, selectedSizeFullData]
  );

  const onWishlistIconClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!wishlistAnimationClass) {
      delayedWishlistHandler(item);
    }
  };

  const toggleSaveToWishList = product => {
    const alreadyInWishlist = isPresentInWishlist(product);

    if (!alreadyInWishlist) {
      setWishlistAnimationClass("addAnimation");
      saveItemToWishList(product, selectedSizeFullData);
    } else {
      setWishlistAnimationClass("removeAnimation");
      deleteFromWishList(product.id, selectedSizeFullData.size.sizeId);
    }
  };

  const delayedWishlistHandler = useCallback(
    debounce(val => {
      toggleSaveToWishList(val);
    }, 0),
    [toggleSaveToWishList]
  );

  const delayedHandleError = useCallback(
    debounce(message => {
      setWishlistAnimationClass("");
      isQuickLookModal
        ? toastMsg(TOAST_TYPES.ERROR, message, true)
        : setAlertMessage(message);
    }, DELAY_TIME),
    [setWishlistAnimationClass]
  );

  const checkStatus = response => {
    const { isToastMessage, message, isNetworkError } =
      checkResponseStatus(response);

    if (isNetworkError || isToastMessage) {
      delayedHandleError(message);
      return { success: false };
    }

    return { success: true };
  };

  const saveItemToWishList = async (item, selectedSizeFullData) => {
    const productForWishList = {
      data: {
        productId: Number(item.id),
        isMultisize: item.isMultisize,
        ...(isEmpty(selectedSizeFullData.sizeType)
          ? { sizeType: "" }
          : {
              sizeType: selectedSizeFullData.sizeType.label,
              sizeTypeKey: selectedSizeFullData.sizeType.key
            }),
        ...(isEmpty(selectedSizeFullData.size)
          ? { size: "", sizeId: 0 }
          : {
              size: selectedSizeFullData.size.size || "",
              sizeId: selectedSizeFullData.size.sizeId || 0
            })
      }
    } as WishlistProductType;

    const response = await dispatch(saveToWishlist(productForWishList));
    const { success } = checkStatus(response);
    if (success) {
      dispatch(getCartAndWishlistGeneralData());
      PdpAnalyticsEvent.addToWishlist({
        item,
        wishlistData: productForWishList.data
      });
      DynamicYieldEventService.addToWishlist({
        productId: item.upc,
        size: selectedSizeFullData.size.size || ""
      });
    }
  };

  const deleteFromWishList = async (productId, sizeId = 0) => {
    const wishListBaseData = getWishListGeneralDataStore();
    const wishListItem =
      !isEmpty(wishListBaseData) &&
      wishListBaseData.find(
        item => item.productId === productId && item.sizeId === sizeId
      );

    PdpAnalyticsEvent.removeFromWishlist({
      item,
      wishListItem
    });
    const response = await dispatch(
      deleteWishListData({
        productId,
        wishlistItemId: wishListItem?._id || ""
      })
    );
    const { success } = checkStatus(response);
    if (success) {
      dispatch(getCartAndWishlistGeneralData());
    }
  };

  const itemPresentInWishlist = isPresentInWishlist(product);

  return (
    <>
      <span
        className={cn(
          "wishlist-icon",
          { "mobile-tablet-view": isMobileView },
          wishlistAnimationClass,
          {
            wishlistIcon: !wishlistAnimationClass && itemPresentInWishlist,
            wishlistedIcon: !wishlistAnimationClass && !itemPresentInWishlist
          }
        )}
        ref={iconRef}
        onClick={onWishlistIconClick}
      />
      <DialogModal
        showModal={Boolean(alertMessage)}
        titleText={t("alert")}
        bodyText={alertMessage}
        confirmButtonText={t("Ok")}
        onConfirm={e => {
          e.stopPropagation();
          setAlertMessage("");
        }}
      />
    </>
  );
};

export default memo(AddToWishlistButton);
